import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  TimePicker,
  Select,
  Button,
  Space,
  message,
  Modal,
  Table,
  Tag,
  Statistic,
  Row,
  Col,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  QrcodeOutlined,
  CloseOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { buildAPIURL } from '../../config/apiConfig';

const { TextArea } = Input;

interface MeetingSession {
  SessionID: number;
  MeetingTitle: string;
  MeetingDescription?: string;
  MeetingDate: string;
  MeetingSession: string;
  StartTime: string;
  Location?: string;
  Status: string;
  TotalAttendees: number;
  CreatedBy: string;
  CreatedAt: string;
}

interface Attendee {
  STT: number;
  Username: string;
  FullName: string;
  RoleName: string;
  CheckInTime: string;
}

const MeetingAttendancePage: React.FC = () => {
  const [form] = Form.useForm();
  const [meetings, setMeetings] = useState<MeetingSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrToken, setQrToken] = useState<string>('');
  const [currentSession, setCurrentSession] = useState<MeetingSession | null>(null);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [qrRefreshKey, setQrRefreshKey] = useState(0);
  const qrIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sseRef = useRef<EventSource | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const buoiOptions = ['Sáng', 'Chiều', 'Tối'];

  // Helper function to get clean token from localStorage
  const getAuthToken = (): string => {
    // Try both possible token keys
    let token = localStorage.getItem('quanly_truonghoc_token') || localStorage.getItem('token');
    
    if (!token) {
      console.error('❌ No token found in localStorage');
      throw new Error('Chưa đăng nhập. Vui lòng đăng nhập lại.');
    }
    
    // Remove surrounding quotes if present (common issue with localStorage)
    if (token.startsWith('"') && token.endsWith('"')) {
      token = token.slice(1, -1);
    }
    
    return token;
  };

  // Load danh sách cuộc họp
  const loadMeetings = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      const response = await fetch(buildAPIURL('/meeting-attendance'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Không thể tải danh sách cuộc họp');

      const data = await response.json();
      if (data.success) {
        setMeetings(data.data || []);
      }
    } catch (err: any) {
      message.error(err.message || 'Lỗi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeetings();
  }, []);

  // Tạo phiên họp mới
  const handleCreateMeeting = async () => {
    try {
      const values = await form.validateFields();
      const token = getAuthToken();

      const payload = {
        meetingTitle: values.meetingTitle,
        meetingDescription: values.meetingDescription,
        meetingDate: (values.meetingDate as Dayjs).format('YYYY-MM-DD'),
        meetingSession: values.meetingSession,
        startTime: (values.startTime as Dayjs).format('HH:mm'),
        location: values.location,
      };

      const response = await fetch(buildAPIURL('/meeting-attendance'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Không thể tạo phiên họp');

      const data = await response.json();
      if (data.success) {
        message.success('Đã tạo phiên họp thành công');
        form.resetFields();
        loadMeetings();
      }
    } catch (err: any) {
      if (err.errorFields) return; // Validation error
      message.error(err.message || 'Lỗi tạo phiên họp');
    }
  };

  // Load danh sách người tham dự
  const loadAttendees = async (sessionId: number) => {
    try {
      const token = getAuthToken();
      const response = await fetch(buildAPIURL(`/meeting-attendance/${sessionId}/attendees`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.success) {
        setAttendees(data.data || []);
      }
    } catch (err) {
      console.error('Error loading attendees:', err);
    }
  };

  // Mở QR cho cuộc họp
  const openQrSession = async (session: MeetingSession) => {
    try {
      setCurrentSession(session);
      setQrModalOpen(true);
      setAttendees([]);

      // Generate initial QR
      await generateQrToken(session.SessionID);

      // Load initial attendees
      await loadAttendees(session.SessionID);

      // Setup SSE for real-time updates (fallback to polling if SSE fails)
      setupSSE(session.SessionID);

      // Setup polling as fallback (every 3 seconds)
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = setInterval(async () => {
        await loadAttendees(session.SessionID);
      }, 3000);

      // Auto-refresh QR every 10s
      if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
      qrIntervalRef.current = setInterval(async () => {
        await generateQrToken(session.SessionID);
        setQrRefreshKey(prev => prev + 1);
      }, 10000);
    } catch (err: any) {
      message.error(err.message || 'Lỗi mở QR');
    }
  };

  // Generate QR Token
  const generateQrToken = async (sessionId: number) => {
    try {
      const token = getAuthToken();
      const response = await fetch(buildAPIURL(`/meeting-attendance/${sessionId}/qr-token`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Không thể tạo mã QR');

      const data = await response.json();
      if (data.success) {
        setQrToken(data.data.token);
      }
    } catch (err) {
      console.error('Error generating QR token:', err);
    }
  };

  // Setup Server-Sent Events for real-time attendance
  const setupSSE = (sessionId: number) => {
    if (sseRef.current) {
      sseRef.current.close();
    }

    const sseUrl = buildAPIURL(`/meeting-attendance/${sessionId}/stream`);
    console.log('🔴 [SSE] Connecting to:', sseUrl);
    
    const eventSource = new EventSource(sseUrl);

    eventSource.onopen = () => {
      console.log('✅ [SSE] Connection opened');
    };

    eventSource.addEventListener('init', (e) => {
      console.log('📨 [SSE] Init event received:', e.data);
      const data = JSON.parse(e.data);
      setAttendees(data.attendees || []);
    });

    eventSource.addEventListener('update', (e) => {
      console.log('📨 [SSE] Update event received:', e.data);
      const data = JSON.parse(e.data);
      setAttendees(data.attendees || []);
      
      // Play sound notification
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZTA0PVqzn77BdGAg+ltryxnMnBSp+zPHaizsIGGS57OihUBELTKXh8bllHAU2jdXyzn0vBSiBz/LajD0KGGu97Oagj0YLElm17O+nWhQKSKDf87pnIgU0idb1zIE0Byxvv+3nol8XC0ml4/G4aR4GOpbW88p3LgUpdMvv25I+CxZqu+3ooVERC02l4vG2Zx8FN4/W88uBMwcrb7/s56FfFwxKp+TxtGgfBTqW1vPKdiwEKnXM7+CPQgwVabzs6aJPEQxOpOLxuGkfBzyU1/PMfzIHK3LA7eejXRYMSqfj8bVpHgU3lNbzzn8xByty8Oznn1AWDUqn4/G2Zx4GOZbX88yAMQYqcsDu5ZJNEQ1Hp+Pxt2cfBjyU1/PLgTMHLHTB7uahUBEMTqji8bpnHgU8lNjzy38wByp0wO/noVERC02n4vG5aR4FO5TX88uBMwcsdMDv5aFQEQxNqOPxuWgeBTuV1/PMgDIHK3LA7+WjTxEMTqjk8bllHgU7ltbzzIAyBit0wO7mpE4RDU2o5PG4aB4FPJbW88yAMQcrdMDv5aJOEA1NqOP=');
      audio.play().catch(() => {});
    });

    eventSource.addEventListener('close', () => {
      console.log('🔴 [SSE] Close event received');
      message.info('Phiên họp đã đóng');
      closeQrModal();
    });

    eventSource.onerror = (err) => {
      console.error('❌ [SSE] Error:', err);
      console.error('❌ [SSE] ReadyState:', eventSource.readyState);
    };

    sseRef.current = eventSource;
  };

  // Đóng phiên họp
  const closeSession = async () => {
    if (!currentSession) return;

    try {
      const token = getAuthToken();
      const response = await fetch(buildAPIURL(`/meeting-attendance/${currentSession.SessionID}/close`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Không thể đóng phiên họp');

      const data = await response.json();
      if (data.success) {
        message.success('Đã đóng phiên họp');
        closeQrModal();
        loadMeetings();
      }
    } catch (err: any) {
      message.error(err.message || 'Lỗi đóng phiên họp');
    }
  };

  // Cleanup khi đóng modal
  const closeQrModal = () => {
    setQrModalOpen(false);
    if (qrIntervalRef.current) {
      clearInterval(qrIntervalRef.current);
      qrIntervalRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    setQrToken('');
    setCurrentSession(null);
    setAttendees([]);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (sseRef.current) sseRef.current.close();
    };
  }, []);

  const columns = [
    {
      title: 'Tiêu đề',
      dataIndex: 'MeetingTitle',
      key: 'MeetingTitle',
      width: 250,
    },
    {
      title: 'Ngày',
      dataIndex: 'MeetingDate',
      key: 'MeetingDate',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      width: 100,
    },
    {
      title: 'Buổi',
      dataIndex: 'MeetingSession',
      key: 'MeetingSession',
      width: 80,
    },
    {
      title: 'Giờ',
      dataIndex: 'StartTime',
      key: 'StartTime',
      render: (time: string) => {
        // Handle ISO datetime string or time-only string
        if (!time) return '-';
        
        // If it's an ISO datetime (contains 'T' or 'Z')
        if (time.includes('T') || time.includes('Z')) {
          return dayjs(time).format('HH:mm');
        }
        
        // If it's already time format (HH:mm:ss or HH:mm)
        return time.substring(0, 5); // Get HH:mm part
      },
      width: 80,
    },
    {
      title: 'Địa điểm',
      dataIndex: 'Location',
      key: 'Location',
      width: 150,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      render: (status: string) => (
        <Tag color={status === 'open' ? 'green' : 'default'}>
          {status === 'open' ? 'Đang mở' : 'Đã đóng'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Số người tham dự',
      dataIndex: 'TotalAttendees',
      key: 'TotalAttendees',
      align: 'center' as const,
      width: 120,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: MeetingSession) => (
        <Space>
          {record.Status === 'open' && (
            <Button
              type="primary"
              icon={<QrcodeOutlined />}
              onClick={() => openQrSession(record)}
              size="small"
            >
              Mở QR
            </Button>
          )}
        </Space>
      ),
      width: 120,
    },
  ];

  const attendeeColumns = [
    { title: 'STT', dataIndex: 'STT', key: 'STT', width: 60 },
    { title: 'Họ và tên', dataIndex: 'FullName', key: 'FullName' },
    { title: 'Username', dataIndex: 'Username', key: 'Username' },
    { title: 'Vai trò', dataIndex: 'RoleName', key: 'RoleName' },
    {
      title: 'Thời gian điểm danh',
      dataIndex: 'CheckInTime',
      key: 'CheckInTime',
      render: (time: string) => {
        if (!time) return '-';
        
        // Remove timezone indicator and parse as local Vietnam time
        // Backend đã lưu theo giờ Việt Nam (UTC+7)
        let cleanTime = time;
        
        // Remove 'Z' or timezone offset if present
        cleanTime = cleanTime.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
        
        // Remove milliseconds if present (2025-10-08T10:09:14.349Z -> 2025-10-08T10:09:14)
        cleanTime = cleanTime.replace(/\.\d{3}/, '');
        
        // Parse as local time (không tự động convert timezone)
        const parsed = dayjs(cleanTime);
        
        return parsed.format('DD/MM/YYYY HH:mm:ss');
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>Điểm danh cuộc họp</h2>

      {/* Form tạo cuộc họp */}
      <Card title="Tạo phiên họp mới" style={{ marginBottom: 24 }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Tiêu đề cuộc họp"
                name="meetingTitle"
                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
              >
                <Input placeholder="VD: Họp đầu năm học 2025-2026" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Địa điểm" name="location">
                <Input placeholder="VD: Hội trường A" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Ngày họp"
                name="meetingDate"
                rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Buổi"
                name="meetingSession"
                rules={[{ required: true, message: 'Vui lòng chọn buổi' }]}
              >
                <Select 
                  options={buoiOptions.map(v => ({ label: v, value: v }))}
                  placeholder="Chọn buổi"
                  dropdownStyle={{ zIndex: 9999 }}
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Giờ bắt đầu"
                name="startTime"
                rules={[{ required: true, message: 'Vui lòng chọn giờ' }]}
              >
                <TimePicker style={{ width: '100%' }} format="HH:mm" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Mô tả" name="meetingDescription">
            <TextArea rows={3} placeholder="Mô tả nội dung cuộc họp" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateMeeting}>
                Tạo phiên họp
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadMeetings}>
                Làm mới
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Danh sách cuộc họp */}
      <Card title="Danh sách cuộc họp">
        <Table
          columns={columns}
          dataSource={meetings}
          rowKey="SessionID"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* QR Modal */}
      <Modal
        title={`Điểm danh cuộc họp: ${currentSession?.MeetingTitle || ''}`}
        open={qrModalOpen}
        onCancel={closeQrModal}
        width={900}
        footer={[
          <Button key="close" danger icon={<CloseOutlined />} onClick={closeSession}>
            Đóng cuộc họp
          </Button>,
        ]}
      >
        {currentSession && (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <div style={{ textAlign: 'center' }}>
                  {qrToken ? (
                    <>
                      <div style={{ marginBottom: 12 }}>
                        <strong>Mã QR hiện tại:</strong> {dayjs().format('HH:mm:ss')}
                        <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                          Tự động thay đổi mỗi 10 giây
                        </div>
                      </div>
                      <img
                        key={qrRefreshKey}
                        alt="qr"
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrToken)}`}
                        style={{ width: 300, height: 300, border: '2px solid #1890ff', borderRadius: 8 }}
                      />
                      <div style={{ marginTop: 12, fontSize: '12px', color: '#888' }}>
                        <div>Quét mã QR này bằng điện thoại để điểm danh</div>
                        <div>Mã QR sẽ tự động thay đổi để đảm bảo bảo mật</div>
                      </div>
                    </>
                  ) : (
                    <Spin size="large" tip="Đang tạo mã QR..." />
                  )}
                </div>
              </Col>

              <Col span={12}>
                <Statistic
                  title="Số người đã tham dự"
                  value={attendees.length}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
                <div style={{ marginTop: 16 }}>
                  <strong>Thông tin cuộc họp:</strong>
                  <div>Ngày: {dayjs(currentSession.MeetingDate).format('DD/MM/YYYY')}</div>
                  <div>Buổi: {currentSession.MeetingSession}</div>
                  <div>Giờ: {currentSession.StartTime}</div>
                  {currentSession.Location && <div>Địa điểm: {currentSession.Location}</div>}
                </div>
              </Col>
            </Row>

            <div style={{ marginTop: 24 }}>
              <h4>Danh sách người tham dự (Real-time)</h4>
              <Table
                columns={attendeeColumns}
                dataSource={attendees}
                rowKey={(record) => `${record.STT}-${record.Username}`}
                pagination={false}
                scroll={{ y: 300 }}
                size="small"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MeetingAttendancePage;
